import { Calendar, DollarSign, Users, FileText, Minus, TrendingUp } from 'lucide-react';
import { format } from 'date-fns';
import { ResourcePayrollData } from '../../lib/payrollRuns';
import { calculatePaystub } from '../../lib/payrollDeductions';

interface PayrollConfirmationProps {
  periodStart: Date;
  periodEnd: Date;
  payrollData: Record<string, ResourcePayrollData>;
  accountantEmail: string | null;
  emailToAccountant: boolean;
  onEmailToAccountantChange: (checked: boolean) => void;
  onConfirm: () => void;
  onGoBack: () => void;
  isProcessing: boolean;
}

export default function PayrollConfirmation({
  periodStart,
  periodEnd,
  payrollData,
  accountantEmail,
  emailToAccountant,
  onEmailToAccountantChange,
  onConfirm,
  onGoBack,
  isProcessing
}: PayrollConfirmationProps) {
  const hasAccountantEmail = Boolean(accountantEmail?.trim());
  const resources = Object.values(payrollData);
  const totalGrossEarnings = resources.reduce((sum, data) => sum + data.totalEarnings, 0);
  
  // Calculate deductions (currently $0 placeholders)
  const paystubCalc = calculatePaystub(totalGrossEarnings);
  const totalDeductions = paystubCalc.deductions.totalDeductions;
  const totalNetPay = paystubCalc.netPay;
  
  const totalEntries = resources.reduce((sum, data) => {
    let entries = data.timeclock.entries.length;
    entries += (data.bonuses?.entries || []).length;
    Object.values(data.bookings).forEach((booking) => {
      entries += booking.items.length;
    });
    return sum + entries;
  }, 0);

  return (
    <div className="space-y-4">
      {/* Summary Card */}
      <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4">
        {/* Period */}
        <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Calendar className="h-4 w-4 text-blue-600" />
          </div>
          <div className="flex-1">
            <p className="text-xs font-medium text-gray-500">Pay Period</p>
            <p className="text-sm font-semibold text-gray-900">
              {format(periodStart, 'MMM d, yyyy')} - {format(periodEnd, 'MMM d, yyyy')}
            </p>
          </div>
        </div>

        {/* Gross Earnings */}
        <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
          <div className="p-2 bg-green-100 rounded-lg">
            <TrendingUp className="h-4 w-4 text-green-600" />
          </div>
          <div className="flex-1">
            <p className="text-xs font-medium text-gray-500">Gross Earnings</p>
            <p className="text-xl font-bold text-gray-900">${totalGrossEarnings.toFixed(2)}</p>
          </div>
        </div>

        {/* Deductions */}
        <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
          <div className="p-2 bg-red-100 rounded-lg">
            <Minus className="h-4 w-4 text-red-600" />
          </div>
          <div className="flex-1">
            <p className="text-xs font-medium text-gray-500">Total Deductions</p>
            <p className="text-sm font-semibold text-gray-900">${totalDeductions.toFixed(2)}</p>
            <p className="text-xs text-gray-400 mt-0.5">Tax: $0.00 • EI: $0.00 • CPP: $0.00</p>
          </div>
        </div>

        {/* Net Pay */}
        <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
          <div className="p-2 bg-blue-100 rounded-lg">
            <DollarSign className="h-4 w-4 text-blue-600" />
          </div>
          <div className="flex-1">
            <p className="text-xs font-medium text-gray-500">Net Pay</p>
            <p className="text-xl font-bold text-blue-600">${totalNetPay.toFixed(2)}</p>
          </div>
        </div>

        {/* Number of Resources */}
        <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Users className="h-4 w-4 text-purple-600" />
          </div>
          <div className="flex-1">
            <p className="text-xs font-medium text-gray-500">Resources</p>
            <p className="text-sm font-semibold text-gray-900">
              {resources.length} {resources.length === 1 ? 'employee' : 'employees'}
            </p>
          </div>
        </div>

        {/* Number of Entries */}
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-100 rounded-lg">
            <FileText className="h-4 w-4 text-orange-600" />
          </div>
          <div className="flex-1">
            <p className="text-xs font-medium text-gray-500">Total Entries</p>
            <p className="text-sm font-semibold text-gray-900">
              {totalEntries} {totalEntries === 1 ? 'entry' : 'entries'} will be marked as PAID
            </p>
          </div>
        </div>
      </div>

      {/* Employee Paystub Previews */}
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Employee Paystub Previews</h4>
        <div className="space-y-2 max-h-[250px] overflow-y-auto">
          {resources.map((data) => {
            const empPaystub = calculatePaystub(data.totalEarnings);
            return (
              <div
                key={data.resource.id}
                className="p-3 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-medium text-sm text-gray-900">
                      {data.resource.first_name} {data.resource.last_name}
                    </p>
                    <p className="text-xs text-gray-500">{data.resource.email}</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <p className="text-gray-500">Gross</p>
                    <p className="font-bold text-gray-900">${empPaystub.grossEarnings.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Deductions</p>
                    <p className="font-semibold text-red-600">-${empPaystub.deductions.totalDeductions.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Net Pay</p>
                    <p className="font-bold text-blue-600">${empPaystub.netPay.toFixed(2)}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Warning Message */}
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 rounded-lg bg-amber-100 p-1">
            <FileText className="h-4 w-4 text-amber-700" />
          </div>
          <div>
            <p className="mb-1 text-sm font-medium text-amber-900">Important</p>
            <p className="text-xs text-amber-800">
              This action will mark all {totalEntries} payroll entries as PAID and cannot be undone.
              Please ensure all information is correct before proceeding.
            </p>
          </div>
        </div>
      </div>

      {/* Email to accountant */}
      <div
        className={`border-t border-gray-200 pt-4 ${!hasAccountantEmail ? 'opacity-50' : ''}`}
      >
        <label
          className={`flex items-start gap-3 select-none ${
            !hasAccountantEmail || isProcessing ? 'cursor-not-allowed' : 'cursor-pointer'
          }`}
        >
          <input
            type="checkbox"
            checked={hasAccountantEmail && emailToAccountant}
            disabled={!hasAccountantEmail || isProcessing}
            onChange={(e) => onEmailToAccountantChange(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
          />
          <div className="min-w-0 flex-1">
            <span className="text-sm font-medium text-gray-900">Email to accountant</span>
            {hasAccountantEmail ? (
              <p className="text-xs text-gray-500 mt-0.5 truncate">{accountantEmail}</p>
            ) : (
              <p className="text-xs text-gray-500 mt-0.5">
                No accountant email configured in payroll settings
              </p>
            )}
            {hasAccountantEmail && emailToAccountant ? (
              <p className="text-xs text-gray-400 mt-1">
                A detailed payroll breakdown PDF will be sent when you run payroll.
              </p>
            ) : null}
          </div>
        </label>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 border-t border-gray-200 pt-4">
        <button
          type="button"
          onClick={onGoBack}
          disabled={isProcessing}
          className="flex-1 rounded-xl bg-gray-100 py-2.5 text-[15px] font-medium text-gray-700 active:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 touch-manipulation"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={isProcessing}
          className="flex-1 rounded-xl bg-blue-600 py-2.5 text-[15px] font-semibold text-white active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50 touch-manipulation"
        >
          {isProcessing ? 'Processing...' : 'Run Payroll'}
        </button>
      </div>
    </div>
  );
}

